import { useState, useEffect, useCallback, useRef } from "react";

interface Approval {
  id: string;
  doc_id?: string;
  eco_id?: string;
  user_id: string;
  seq: number;
  decision: string;
  comment: string | null;
  decided_at: string | null;
  user_name: string;
  user_email: string;
}

interface ApprovalSectionProps {
  resourceType: "document" | "eco";
  resourceId: string;
  onUpdate?: () => void;
}

const DECISION_COLORS: Record<string, { bg: string; fg: string }> = {
  approved: { bg: "rgba(60,180,80,0.15)", fg: "#2a9d3a" },
  rejected: { bg: "rgba(220,60,60,0.15)", fg: "#c44" },
  pending: { bg: "rgba(160,160,160,0.15)", fg: "#999" },
};

export default function ApprovalSection({
  resourceType,
  resourceId,
  onUpdate,
}: ApprovalSectionProps) {
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [decideComment, setDecideComment] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const userFetched = useRef(false);

  const basePath =
    resourceType === "document"
      ? `/api/approvals/${resourceId}`
      : `/api/ecos/${resourceId}/approvals`;

  // Fetch current user
  useEffect(() => {
    if (userFetched.current) return;
    userFetched.current = true;
    fetch("/api/user/me", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setCurrentUserId(data?.user?.id ?? null))
      .catch(() => {});
  }, []);

  const fetchApprovals = useCallback(async () => {
    try {
      const res = await fetch(basePath, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load approvals");
      setApprovals(await res.json());
      setError("");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [basePath]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleAdd = useCallback(async () => {
    if (!addEmail.trim()) return;
    setAdding(true);
    setError("");
    try {
      const userId = addEmail.trim();
      const res = await fetch(basePath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: userId }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.error || `Add failed (${res.status})`);
      }
      setShowAdd(false);
      setAddEmail("");
      await fetchApprovals();
      onUpdate?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }, [addEmail, basePath, fetchApprovals, onUpdate]);

  const handleRemove = useCallback(
    async (approvalId: string) => {
      setError("");
      try {
        const res = await fetch(`${basePath}/${approvalId}`, {
          method: "DELETE",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Remove failed");
        await fetchApprovals();
        onUpdate?.();
      } catch (err: any) {
        setError(err.message);
      }
    },
    [basePath, fetchApprovals, onUpdate]
  );

  const handleDecide = useCallback(
    async (decision: "approved" | "rejected") => {
      setDeciding(true);
      setError("");
      try {
        const decidePath =
          resourceType === "document"
            ? `/api/approvals/${resourceId}/decide`
            : `/api/ecos/${resourceId}/approvals/decide`;
        const res = await fetch(decidePath, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ decision, comment: decideComment || undefined }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Decision failed (${res.status})`);
        }
        setDecideComment("");
        await fetchApprovals();
        onUpdate?.();
      } catch (err: any) {
        setError(err.message);
      } finally {
        setDeciding(false);
      }
    },
    [resourceType, resourceId, decideComment, fetchApprovals, onUpdate]
  );

  const isPendingApprover =
    currentUserId &&
    approvals.some((a) => a.user_id === currentUserId && a.decision === "pending");

  const approvedCount = approvals.filter((a) => a.decision === "approved").length;

  if (loading) return null;

  return (
    <div className="approval-section">
      <div className="release-section-header">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M5 8l2 2 4-4" />
          <circle cx="8" cy="8" r="6" />
        </svg>
        Approvals
        {approvals.length > 0 && (
          <span className="approval-count">
            {approvedCount}/{approvals.length}
          </span>
        )}
        <button
          className="bom-btn"
          style={{ marginLeft: "auto", fontSize: 11 }}
          onClick={() => setShowAdd(!showAdd)}
        >
          + Add
        </button>
      </div>

      {error && <div className="release-error" style={{ marginBottom: 6 }}>{error}</div>}

      {showAdd && (
        <div className="approval-add-form">
          <input
            className="release-field-input"
            type="text"
            placeholder="User ID..."
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          />
          <button
            className="release-save-btn"
            onClick={handleAdd}
            disabled={adding || !addEmail.trim()}
            style={{ fontSize: 11, height: 26 }}
          >
            {adding ? "..." : "Add"}
          </button>
        </div>
      )}

      {approvals.length === 0 ? (
        <div className="approval-empty">No approvers configured</div>
      ) : (
        <div className="approval-list">
          {approvals.map((a) => {
            const colors = DECISION_COLORS[a.decision] || DECISION_COLORS.pending;
            return (
              <div key={a.id} className="approval-row">
                <div className="approval-avatar">
                  {(a.user_name || a.user_email || "?")[0].toUpperCase()}
                </div>
                <div className="approval-info">
                  <span className="approval-name">{a.user_name || a.user_email}</span>
                  {a.comment && (
                    <span className="approval-comment" title={a.comment}>
                      {a.comment}
                    </span>
                  )}
                </div>
                <span
                  className="bom-state-badge"
                  style={{ background: colors.bg, color: colors.fg, fontSize: 10 }}
                >
                  {a.decision}
                </span>
                <button
                  className="bom-delete-btn"
                  onClick={() => handleRemove(a.id)}
                  title="Remove"
                >
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {isPendingApprover && (
        <div className="approval-decide">
          <textarea
            className="release-field-input"
            placeholder="Optional comment..."
            value={decideComment}
            onChange={(e) => setDecideComment(e.target.value)}
            rows={2}
            style={{ resize: "vertical", fontFamily: "var(--mono)" }}
          />
          <div className="approval-decide-buttons">
            <button
              className="release-action-btn release-action-btn--success"
              onClick={() => handleDecide("approved")}
              disabled={deciding}
              style={{ height: 28, fontSize: 11 }}
            >
              Approve
            </button>
            <button
              className="release-action-btn release-action-btn--danger"
              onClick={() => handleDecide("rejected")}
              disabled={deciding}
              style={{ height: 28, fontSize: 11 }}
            >
              Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function useApprovalStatus(documentId: string) {
  const [approvals, setApprovals] = useState<Approval[]>([]);

  useEffect(() => {
    fetch(`/api/approvals/${documentId}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setApprovals)
      .catch(() => {});
  }, [documentId]);

  const total = approvals.length;
  const approved = approvals.filter((a) => a.decision === "approved").length;
  const hasPending = approvals.some((a) => a.decision === "pending");
  const allApproved = total > 0 && approved === total;

  return { total, approved, hasPending, allApproved };
}
